import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useRpcReadAccess } from 'src/app/AppAuth/useReadAccess'
import { useAccessRoles } from 'src/pages/Users/users.hooks'
import { useCanPerformActionChecker } from 'src/app/AppAuth/AppAuth.hooks'
import { useFilterHideList } from 'src/context/FilterHideListContext'
import { Search } from '../../Search/Search'
import { RequestAssigneeFilterDropdown } from './FilterDropdowns/RequestAssigneeFilterDropdown'
import { filterRequiredCapabilities } from './FilterPermissions.config'
import {
  CompactFiltersTextButton,
  FilterBarWrapper,
  FilterSearchContainer,
} from './FilterBar.styles'
import {
  AuditFilterDropdown,
  AuditFrameworksFilterDropdown,
  AuditStatusFilterDropdown,
  DepartmentFilterDropdown,
  HasDocumentationFilterDropdown,
  OwnerFilterDropdown,
  PolicyFilterDropdown,
  StatusFilterDropdown,
  OrganizationFilterDropdown,
  QuestionnaireStatusFilterDropdown,
  QuestionnaireVendorFilterDropdown,
  QuestionnaireAssigneeFilterDropdown,
  QuestionnaireDateRangeFilter,
  AuditRunOwnerFilterDropdown,
  AuditRunControlFilterDropdown,
  AuditRunStatusFilterDropdown,
  AuditRunResultFilterDropdown,
  ReportPlaybookFilterDropdown,
  ExecutionTypeFilterDropdown,
  AccountTypeFilterDropdown,
  CitationTypesFilterDropdown,
  TrusteroUserFilterDropdown,
} from './FilterDropdowns'
import { AssigneeFilterDropdown } from './FilterDropdowns/AssigneeFilterDropdown'
import { AUDIT_DEFAULT_NONE } from './FilterDropdowns/AuditFilterDropdown'
import { ClearAllFilters } from './FilterBar.components'
import { FilterBarProps, FilterName, FilterParam } from './FilterBar.types'
import { FilterBarConfig } from './FilterBar.constants'
import { RiskFilterDropdown } from './FilterDropdowns/RiskFilterDropdown'
import { ReviewedFilterDropdown } from './FilterDropdowns/ReviewedFilterDropdown'
import { ActiveFilterDropdown } from './FilterDropdowns/ActiveFilterDropdown'
import { RiskCategoryFilterDropdown } from './FilterDropdowns/RiskCategoryFilterDropdown'
import { ReceptorFilterDropdown } from './FilterDropdowns/ReceptorFilterDropdown'
import { ServiceRoleFilterDropdown } from './FilterDropdowns/ServiceRoleFilterDropdown'
import { RiskTierFilterDropdown } from './FilterDropdowns/RiskTierFilterDropdown'
import { VendorStatusFilterDropdown } from './FilterDropdowns/VendorStatusFilterDropdown'
import { VendorStateFilterDropdown } from './FilterDropdowns/VendorStateFilterDropdown'
import { VendorEvaluationFilterDropdown } from './FilterDropdowns/VendorEvaluationFilterDropdown'
import { AttestationFilterDropdown } from './FilterDropdowns/AttestationFilterDropdown'
import { DocumentScanFilterDropdown } from './FilterDropdowns/DocumentScanFilterDropdown'
import { RequestTypeFilterDropdown } from './FilterDropdowns/RequestTypeFilterDropdown'
import { FolderScanFilterDropdown } from './FilterDropdowns/FolderScanFilterDropdown'
import { SmartChecksFilterDropdown } from './FilterDropdowns/SmartChecksFilterDropdown'
import { SourceFilterDropdown } from './FilterDropdowns/SourceFilterDropdown'
import { RequestSourceFilterDropdown } from './FilterDropdowns/RequestSourceFilterDropdown'
import { DateRangeFilter } from './DateRangeFilter'
import { ControlFilterDropdown } from './FilterDropdowns/ControlFilterDropdown'
import { AssociatedRiskFilterDropdown } from './FilterDropdowns/AssociatedRiskFilterDropdown'
import { FrameworkNameFilterDropdown } from './FilterDropdowns/FrameworkNameFilterDropdown'
import { LabelFilterDropdown } from './FilterDropdowns/LabelFilterDropdown'
import { FrameworkSectionFilterDropdown } from './FilterDropdowns/FrameworkSectionFilterDropdown'
import { FrameworkTypeFilterDropdown } from './FilterDropdowns/FrameworkTypeFilterDropdown'
import { EvidenceTypeFilterDropdown } from './FilterDropdowns/EvidenceTypeFilterDropdown'
import { ComplianceFrameworkFilterDropdown } from './FilterDropdowns/ComplianceFrameworkFilterDropdown'
import { TrustPortalRequestAccessDropdown } from './FilterDropdowns/TrustPortalRequestAccessDropdown'
import { TrustPortalPermissionDropdown } from './FilterDropdowns/TrustPortalPermissionDropdown'
import { CompanyFilterDropdown } from './FilterDropdowns/CompanyFilterDropdown'
import { ReportTypeFilterDropdown } from './FilterDropdowns/ReportTypeFilterDropdown'
import { TrustServiceCriteriaFilterDropdown } from './FilterDropdowns/TrustServiceCriteriaFilterDropdown'
import { AuditorFilterDropdown } from './FilterDropdowns/AuditorFilterDropdown'
import { ExpirationDateFilter } from './FilterDropdowns/ExpirationDateFilter'
import { UserRoleFilterDropdown } from './FilterDropdowns/UserRoleFilterDropdown'
import { EmailNotificationFilterDropdown } from './FilterDropdowns/EmailNotificationFilterDropdown'
import { MoreFiltersDropdown } from './MoreFiltersDropdown'
import { UserDepartmentFilterDropdown } from './FilterDropdowns/UserDepartmentFilterDropdown'
import { UserStatusFilterDropdown } from './FilterDropdowns/UserStatusFilterDropdown'
import { DashboardPolicyFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardPolicyFilterDropdown'
import { DashboardAuditFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardAuditFilterDropdown'
import { DashboardRiskFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardRiskFilterDropdown'
import { DashboardDepartmentFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/DashboardDepartmentFilterDropdown'
import { DashboardOwnerFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardAssigneeFilterDropdown'
import { DashboardFrameworkFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardFrameworkFilterDropdown'
import { DashboardDateRangeFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDateRangeFilterDropdown/DashboardDateRangeFilterDropdown'
import { DashboardControlFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardControlFilterDropdown'
import { DashboardRiskCategoryFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardRiskCategoryFilterDropdown'
import { DashboardVendorFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardVendorFilterDropdown'
import { DashboardLabelFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardLabelFilterDropdown'
import { TrustPortalFeaturesDropdown } from './FilterDropdowns/TrustPortalFeaturesDropdown'
import { DashboardDocNameFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/DashboardDocNameFilterDropdown'
import { DashboardNameFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/DashboardNameFilterDropdown'
import { DashboardEmailFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/DashboardEmailFilterDropdown'
import { DashboardCompanyFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/DashboardCompanyFilterDropdown'
import { DashboardTPFeatureFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/TrustDimensionFilterDropdowns/DashboardTPFeatureFilterDropdown'
import { DashboardTPAccessLevelFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/TrustDimensionFilterDropdowns/DashboardTPAccessLevelFilterDropdown'
import { DashboardTPUpdateCategoryFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDimensionFilterDropdowns/TrustDimensionFilterDropdowns/DashboardTPUpdateCategoryFilterDropdown'
import { AccessRoleFilterDropdown } from './FilterDropdowns/AccessRoleFilterDropdown'
import { PlaybookTypeFilterDropdown } from './FilterDropdowns/PlaybookTypeFilterDropdown'
import { GenericFilterDropdown } from './FilterDropdowns/GenericFilterDropdown'
import { OverviewFrameworkFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/OverviewFrameworkFilterDropdown'
import { OverviewDashboardDateRangeFilterDropdown } from './FilterDropdowns/DashboardFilterDropdowns/OverviewDashboardDateRangeFilterDropdown'
import { DashboardDateRangeDefaults } from './FilterDropdowns/DashboardFilterDropdowns/DashboardDateRangeFilterDropdown/DashboardDateRangeFilterDropdown.types'
import { FilterLabelsProvider } from './FilterLabelsContext'
import { SavedFiltersMenu } from './SavedFiltersMenu'

// Priority order for filters - higher priority filters will be shown first
// Typed from FilterBarConfig keys for type safety
export const FILTER_PRIORITY: (keyof FilterBarConfig)[] = [
  'frameworkName',
  'requestType',
  'department',
  'status',
  'active',
  'accountType',
  'trusteroUser',
  'owner',
  'assignee',
  'activityActor',
  'activityObjectType',
  'citationPolicy',
  'citationControl',
  'citationEvidence',
  'citationRequest',
  'citationRisk',
  'citationTypes',
  'audit',
  'policy',
  'label',
  'associatedRisk',
  'section',
  'organization',
  'questionnaireVendor',
  'questionnaireStatus',
  'questionnaireAssignee',
  'questionnaireDateRange',
  'complianceFramework',
  'auditFrameworks',
  'frameworkType',
  'riskCategory',
  'risk',
  'reviewed',
  'receptor',
  'serviceRole',
  'riskTier',
  'vendorStatus',
  'vendorState',
  'vendorEvaluation',
  'attestation',
  'smartChecks',
  'source',
  'evidenceType',
  'fileType',
  'folderScan',
  'dateRange',
  'control',
  'trustPortalRequestAccess',
  'trustPortalPermission',
  'trustPortalFeatures',
  'documentation',
  'audit_run_owner',
  'audit_run_control',
  'audit_run_status',
  'audit_run_result',
  'company',
  'reportType',
  'trustServiceCriteria',
  'auditor',
  'expirationDate',
  'userDepartment',
  'userRole',
  'emailNotification',
  'userStatus',
  'auditStatus',
  'dashboardFramework',
  'dashboardPolicy',
  'dashboardAudit',
  'dashboardRisk',
  'dashboardDepartment',
  'dashboardRiskCategory',
  'dashboardVendor',
  'dashboardControl',
  'dashboardOwner',
  'dashboardLabel',
  'dashboardDateRange',
  'reportPlaybook',
  'dashboardName',
  'dashboardEmail',
  'dashboardCompany',
  'dashboardDocName',
  'dashboardTPFeature',
  'dashboardTPAccessLevel',
  'dashboardTPUpdateCategory',
  'executionType',
  'playbookType',
  'playbookCategory',
  'visibilities',
  'tags',
  'documentScan',
  'overviewFramework',
  'overviewDateRange',
  'activitiesDateRange',
  'requestSource',
]

export const FilterBar = ({
  config,
  page,
  searchBarOptions,
  resizeNodeId,
  leadingElement,
  customElement,
  dashboardContext,
  dashboardName,
  useSeeFiltersWhenOverflow,
  disableSavedFilters,
}: FilterBarProps): JSX.Element => {
  // Filter visibility is configured per index page in Account Settings, and
  // pages or tabs pass `page` (e.g. Controls and Controls by Requirement both use
  // Controls) to respect configured filter visibility. Filter bars without it -
  // modals, dashboards, tabs without configurable filters - keep showing every
  // filter their config enables.
  const { isFilterHidden } = useFilterHideList()

  const { data: accessRolesData } = useAccessRoles()
  const hasAccessRoles = useMemo(() => {
    const accessRoles = accessRolesData?.getAccessRolesList()
    if (!accessRoles) return false
    return accessRoles.length > 0
  }, [accessRolesData])
  const canPerformAction = useCanPerformActionChecker()
  const canReadRequestGroups = useRpcReadAccess(
    '/request.Request/ListAssigneeGroups',
  )
  // The Requests assignee filter also lists user groups, so it shows for a
  // caller who can read groups without seeing users.
  const canShowFilter = useCallback(
    (filterKey: keyof FilterBarConfig) =>
      (filterKey === 'owner' && !!config.ownerGroups && canReadRequestGroups) ||
      [getFilterParamFromKey(filterKey)]
        .flat()
        .flatMap(filterRequiredCapabilities)
        .every((capability) => canPerformAction(capability)),
    [config.ownerGroups, canReadRequestGroups, canPerformAction],
  )
  const [visibleFilters, setVisibleFilters] = useState<
    (keyof FilterBarConfig)[]
  >([])
  const [hiddenFilters, setHiddenFilters] = useState<(keyof FilterBarConfig)[]>(
    [],
  )
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false)
  const filterBarRef = useRef<HTMLUListElement>(null)
  const leadingElementRef = useRef<HTMLDivElement>(null)

  // Get all enabled filters based on config
  const getEnabledFilters = useCallback(() => {
    const enabledFilters = FILTER_PRIORITY.filter(
      (filterKey) =>
        config[filterKey as keyof typeof config] &&
        canShowFilter(filterKey as keyof FilterBarConfig) &&
        (!page || !isFilterHidden(page, filterKey)),
    )
    if (
      enabledFilters.includes('documentScan') &&
      enabledFilters.includes('status')
    ) {
      return [
        'documentScan',
        'status',
        ...enabledFilters.filter(
          (filterKey) => filterKey !== 'documentScan' && filterKey !== 'status',
        ),
      ] as (keyof FilterBarConfig)[]
    }
    return enabledFilters as (keyof FilterBarConfig)[]
  }, [config, canShowFilter, page, isFilterHidden])

  // Check if filters fit in the container
  const checkFiltersFit = useCallback(() => {
    if (!filterBarRef.current) return

    const container = filterBarRef.current
    const containerWidth = container.offsetWidth

    // Reserve space for "More filters" dropdown and some padding
    const reservedWidth = 140 + 16 // More filters button width + padding
    const reservedWidthPlusSearchWidth = reservedWidth + 741 // With search expanded
    // leadingElement (e.g. the dashboard group-by dropdown) shares the row
    // with filters, so its rendered width must come out of the budget too.
    const leadingWidth = leadingElementRef.current?.offsetWidth ?? 0

    const availableWidth = isSearchExpanded
      ? containerWidth - reservedWidthPlusSearchWidth - leadingWidth
      : containerWidth - reservedWidth - leadingWidth

    const enabledFilters = getEnabledFilters()
    let currentWidth = 80 // make room for 'Clear filters' button
    const newVisibleFilters: (keyof FilterBarConfig)[] = []
    const newHiddenFilters: (keyof FilterBarConfig)[] = []

    // Estimate width for each filter based on filter name length
    const getFilterWidth = (filterKey: keyof FilterBarConfig) => {
      const baseWidth = 80 // Base width for filter button
      const charWidth = 8 // Approximate width per character
      const filterName = getFilterDisplayName(filterKey)
      return baseWidth + filterName.length * charWidth + 8 // Add gap
    }

    for (const filterKey of enabledFilters) {
      const filterWidth = getFilterWidth(filterKey)
      currentWidth += filterWidth

      if (currentWidth <= availableWidth || !newVisibleFilters.length) {
        // we want at least one filter visible (other than 'more filters')
        newVisibleFilters.push(filterKey)
      } else {
        newHiddenFilters.push(filterKey)
      }
    }

    if (newHiddenFilters.length === 1) {
      // if there is only one hidden filter, show it in the visible filters
      newVisibleFilters.push(...newHiddenFilters)
      newHiddenFilters.pop()
    }

    setVisibleFilters(newVisibleFilters)
    setHiddenFilters(newHiddenFilters)
  }, [getEnabledFilters, isSearchExpanded])

  useEffect(() => {
    checkFiltersFit()

    const handleResize = () => {
      checkFiltersFit()
    }

    const node = resizeNodeId ? document.getElementById(resizeNodeId) : null
    const observer = new MutationObserver(handleResize)
    if (node) {
      observer.observe(node, {
        subtree: true,
        childList: true,
      })
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      observer.disconnect()
    }
  }, [config, checkFiltersFit, isSearchExpanded, resizeNodeId])

  // Re-fit when the leading element's box changes: its inner content can
  // reflow (e.g. group-by dropdown switching label text) without triggering
  // any of the deps above. Gated on presence so we only (re)observe on toggle.
  const hasLeadingElement = !!leadingElement
  useEffect(() => {
    const node = leadingElementRef.current
    if (!node) return
    const ro = new ResizeObserver(() => checkFiltersFit())
    ro.observe(node)
    return () => ro.disconnect()
  }, [checkFiltersFit, hasLeadingElement])

  const renderFilter = useCallback(
    (filterKey: keyof FilterBarConfig, isNested = false) => {
      if (!canShowFilter(filterKey)) return null
      switch (filterKey) {
        case 'department':
          return (
            <DepartmentFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'policy':
          return <PolicyFilterDropdown key={filterKey} isNested={isNested} />
        case 'status':
          return <StatusFilterDropdown key={filterKey} isNested={isNested} />
        case 'active':
          return <ActiveFilterDropdown key={filterKey} isNested={isNested} />
        case 'owner':
          if (config.ownerGroups) {
            return (
              <RequestAssigneeFilterDropdown
                key={filterKey}
                isNested={isNested}
                customName={config.ownerLabel}
              />
            )
          }
          return (
            <OwnerFilterDropdown
              key={filterKey}
              isNested={isNested}
              customName={config.ownerLabel}
            />
          )
        case 'assignee':
          return <AssigneeFilterDropdown key={filterKey} isNested={isNested} />
        case 'questionnaireVendor':
          return (
            <QuestionnaireVendorFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'questionnaireAssignee':
          return (
            <QuestionnaireAssigneeFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'documentation':
          return (
            <React.Fragment key={filterKey}>
              <HasDocumentationFilterDropdown
                param={FilterParam.HAS_DOCUMENTATION}
                name={FilterName.HAS_DOCUMENTATION}
                isNested={isNested}
              />
              <HasDocumentationFilterDropdown
                param={FilterParam.IS_CUSTOMIZED}
                name={FilterName.IS_CUSTOMIZED}
                isNested={isNested}
              />
            </React.Fragment>
          )
        case 'complianceFramework':
          return (
            <ComplianceFrameworkFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'audit':
          return (
            <AuditFilterDropdown
              key={filterKey}
              isNested={isNested}
              includeNone={config.auditIncludeNone}
              noneLabel={config.auditNoneLabel}
              includeClosed={config.auditIncludeClosed}
              defaultSelection={
                config.auditDefaultNone ? AUDIT_DEFAULT_NONE : undefined
              }
            />
          )
        case 'auditFrameworks':
          return (
            <AuditFrameworksFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'auditStatus':
          return (
            <AuditStatusFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'risk':
          return (
            <React.Fragment key={filterKey}>
              <RiskFilterDropdown
                param={FilterParam.INHERENT_RISK}
                name={FilterName.INHERENT_RISK}
                isNested={isNested}
              />
              <RiskFilterDropdown
                param={FilterParam.RESIDUAL_RISK}
                name={FilterName.RESIDUAL_RISK}
                isNested={isNested}
              />
            </React.Fragment>
          )
        case 'reviewed':
          return <ReviewedFilterDropdown key={filterKey} isNested={isNested} />
        case 'riskCategory':
          return (
            <RiskCategoryFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'receptor':
          return <ReceptorFilterDropdown key={filterKey} isNested={isNested} />
        case 'serviceRole':
          return (
            <ServiceRoleFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'riskTier':
          return <RiskTierFilterDropdown key={filterKey} isNested={isNested} />
        case 'vendorStatus':
          return (
            <VendorStatusFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'vendorState':
          return (
            <VendorStateFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'vendorEvaluation':
          return (
            <VendorEvaluationFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'attestation':
          return (
            <AttestationFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'smartChecks':
          return (
            <SmartChecksFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'source':
          return <SourceFilterDropdown key={filterKey} isNested={isNested} />
        case 'requestSource':
          return (
            <RequestSourceFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'evidenceType':
          return (
            <EvidenceTypeFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'folderScan':
          return (
            <FolderScanFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'dateRange':
          return (
            <DateRangeFilter
              key={filterKey}
              isNested={isNested}
              title={config.dateRangeTitle}
            />
          )
        case 'questionnaireDateRange':
          return (
            <QuestionnaireDateRangeFilter key={filterKey} isNested={isNested} />
          )
        case 'control':
          return (
            <ControlFilterDropdown
              key={filterKey}
              isNested={isNested}
              includeNone={config.controlIncludeNone}
            />
          )
        case 'associatedRisk':
          return (
            <AssociatedRiskFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'frameworkType':
          return (
            <FrameworkTypeFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'frameworkName':
          return (
            <FrameworkNameFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'label':
          return <LabelFilterDropdown key={filterKey} isNested={isNested} />
        case 'section':
          return (
            <FrameworkSectionFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )

        case 'trustPortalRequestAccess':
          return (
            <TrustPortalRequestAccessDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'trustPortalPermission':
          return (
            <TrustPortalPermissionDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'trustPortalFeatures':
          return (
            <TrustPortalFeaturesDropdown key={filterKey} isNested={isNested} />
          )
        case 'organization':
          return (
            <OrganizationFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'questionnaireStatus':
          return (
            <QuestionnaireStatusFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'audit_run_owner':
          return (
            <AuditRunOwnerFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'audit_run_control':
          return (
            <AuditRunControlFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'audit_run_status':
          return (
            <AuditRunStatusFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'audit_run_result':
          return (
            <AuditRunResultFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'company':
          return <CompanyFilterDropdown key={filterKey} isNested={isNested} />
        case 'reportType':
          return (
            <ReportTypeFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'trustServiceCriteria':
          return (
            <TrustServiceCriteriaFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'auditor':
          return <AuditorFilterDropdown key={filterKey} isNested={isNested} />
        case 'expirationDate':
          return <ExpirationDateFilter key={filterKey} isNested={isNested} />
        case 'userRole':
          return hasAccessRoles ? (
            <AccessRoleFilterDropdown key={filterKey} isNested={isNested} />
          ) : (
            <UserRoleFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'emailNotification':
          return (
            <EmailNotificationFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'userDepartment':
          return (
            <UserDepartmentFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'userStatus':
          return (
            <UserStatusFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'accountType':
          return (
            <AccountTypeFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'trusteroUser':
          return (
            <TrusteroUserFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'dashboardFramework':
          return (
            <DashboardFrameworkFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardPolicy':
          return (
            <DashboardPolicyFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardAudit':
          return <DashboardAuditFilterDropdown isNested={isNested} />
        case 'dashboardRisk':
          return (
            <DashboardRiskFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardDepartment':
          return (
            <DashboardDepartmentFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardOwner':
          return (
            <DashboardOwnerFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardDateRange':
          return <DashboardDateRangeFilterDropdown />
        case 'dashboardControl':
          return (
            <DashboardControlFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardRiskCategory':
          return (
            <DashboardRiskCategoryFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardVendor':
          return (
            <DashboardVendorFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardLabel':
          return (
            <DashboardLabelFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'reportPlaybook':
          return <ReportPlaybookFilterDropdown isNested={isNested} />
        case 'dashboardName':
          return (
            <DashboardNameFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardEmail':
          return (
            <DashboardEmailFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardCompany':
          return (
            <DashboardCompanyFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardDocName':
          return (
            <DashboardDocNameFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'dashboardTPFeature':
          return <DashboardTPFeatureFilterDropdown isNested={isNested} />
        case 'dashboardTPAccessLevel':
          return <DashboardTPAccessLevelFilterDropdown isNested={isNested} />
        case 'dashboardTPUpdateCategory':
          return (
            <DashboardTPUpdateCategoryFilterDropdown
              isNested={isNested}
              dashboardContext={dashboardContext}
              dashboardName={dashboardName}
            />
          )
        case 'executionType':
          return <ExecutionTypeFilterDropdown isNested={isNested} />
        case 'playbookType':
          return <PlaybookTypeFilterDropdown isNested={isNested} />
        case 'playbookCategory':
          return (
            <GenericFilterDropdown
              filterParam={FilterParam.PLAYBOOK_CATEGORY}
              filterName={FilterName.PLAYBOOK_CATEGORY}
              valueKey="playbookCategories"
              isNested={isNested}
            />
          )
        case 'activityActor':
          return (
            <GenericFilterDropdown
              filterParam={FilterParam.ACTIVITY_ACTOR}
              filterName={FilterName.ACTIVITY_ACTOR}
              valueKey="activityActors"
              isNested={isNested}
            />
          )
        case 'activityObjectType':
          return (
            <GenericFilterDropdown
              filterParam={FilterParam.ACTIVITY_OBJECT_TYPE}
              filterName={FilterName.ACTIVITY_OBJECT_TYPE}
              valueKey="activityObjectTypes"
              isNested={isNested}
            />
          )
        case 'citationPolicy':
          return (
            <GenericFilterDropdown
              filterParam={FilterParam.CITATION_POLICY}
              filterName={FilterName.CITATION_POLICY}
              valueKey="citationPolicies"
              isNested={isNested}
            />
          )
        case 'citationControl':
          return (
            <GenericFilterDropdown
              filterParam={FilterParam.CITATION_CONTROL}
              filterName={FilterName.CITATION_CONTROL}
              valueKey="citationControls"
              isNested={isNested}
            />
          )
        case 'citationEvidence':
          return (
            <GenericFilterDropdown
              filterParam={FilterParam.CITATION_EVIDENCE}
              filterName={FilterName.CITATION_EVIDENCE}
              valueKey="citationEvidences"
              isNested={isNested}
            />
          )
        case 'citationRequest':
          return (
            <GenericFilterDropdown
              filterParam={FilterParam.CITATION_REQUEST}
              filterName={FilterName.CITATION_REQUEST}
              valueKey="citationRequests"
              isNested={isNested}
            />
          )
        case 'citationRisk':
          return (
            <GenericFilterDropdown
              filterParam={FilterParam.CITATION_RISK}
              filterName={FilterName.CITATION_RISK}
              valueKey="citationRisks"
              isNested={isNested}
            />
          )
        case 'citationTypes':
          return (
            <CitationTypesFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'documentScan':
          return (
            <DocumentScanFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'requestType':
          return (
            <RequestTypeFilterDropdown key={filterKey} isNested={isNested} />
          )
        case 'overviewFramework':
          return (
            <OverviewFrameworkFilterDropdown
              key={filterKey}
              isNested={isNested}
            />
          )
        case 'overviewDateRange':
          return <OverviewDashboardDateRangeFilterDropdown key={filterKey} />
        case 'activitiesDateRange':
          return (
            <OverviewDashboardDateRangeFilterDropdown
              key={filterKey}
              includeAllTime
              defaultRange={DashboardDateRangeDefaults.LAST_90_DAYS}
            />
          )
        default:
          return null
      }
    },
    [
      canShowFilter,
      dashboardContext,
      dashboardName,
      hasAccessRoles,
      config.ownerLabel,
      config.ownerGroups,
      config.auditIncludeNone,
      config.auditNoneLabel,
      config.auditIncludeClosed,
      config.auditDefaultNone,
      config.controlIncludeNone,
      config.dateRangeTitle,
    ],
  )

  const hiddenFiltersElements = useMemo(
    () =>
      hiddenFilters.map((filterKey) => ({
        id: getFilterParamFromKey(filterKey),
        element: renderFilter(filterKey, true),
      })),
    [hiddenFilters, renderFilter],
  )

  const visibleFiltersElements = useMemo(
    () => visibleFilters.map((filterKey) => renderFilter(filterKey)),
    [visibleFilters, renderFilter],
  )

  return (
    <FilterLabelsProvider>
      <FilterSearchContainer
        $isSearchExpanded={isSearchExpanded}
        ref={filterBarRef}
      >
        {useSeeFiltersWhenOverflow &&
        Boolean(searchBarOptions) &&
        isSearchExpanded &&
        hiddenFilters.length > 0 ? (
          <CompactFiltersTextButton
            type="button"
            onClick={() => setIsSearchExpanded(false)}
          >
            See filters
          </CompactFiltersTextButton>
        ) : (
          <FilterBarWrapper>
            {leadingElement && (
              <div ref={leadingElementRef}>{leadingElement}</div>
            )}
            {visibleFiltersElements}
            {hiddenFilters.length > 0 && (
              <MoreFiltersDropdown filters={hiddenFiltersElements} />
            )}
            <ClearAllFilters />
            {!disableSavedFilters && <SavedFiltersMenu />}
          </FilterBarWrapper>
        )}
        {searchBarOptions && (
          <Search
            setIsExpanded={setIsSearchExpanded}
            isExpanded={isSearchExpanded}
            withContentSearch={searchBarOptions.withContentSearch}
            withAdvancedSearch
          />
        )}
        {customElement}
      </FilterSearchContainer>
    </FilterLabelsProvider>
  )
}

// Get display name for filter
export const getFilterDisplayName = (
  filterKey: keyof FilterBarConfig,
): string => {
  switch (filterKey) {
    case 'department':
      return FilterName.DEPARTMENT
    case 'policy':
      return FilterName.POLICY
    case 'status':
      return FilterName.STATUS
    case 'active':
      return FilterName.ACTIVE
    case 'owner':
      return FilterName.OWNER
    case 'assignee':
      return FilterName.ASSIGNEE
    case 'questionnaireVendor':
      return FilterName.QUESTIONNAIRE_VENDOR
    case 'questionnaireAssignee':
      return FilterName.QUESTIONNAIRE_ASSIGNEE
    case 'activityActor':
      return FilterName.ACTIVITY_ACTOR
    case 'activityObjectType':
      return FilterName.ACTIVITY_OBJECT_TYPE
    case 'citationPolicy':
      return FilterName.CITATION_POLICY
    case 'citationControl':
      return FilterName.CITATION_CONTROL
    case 'citationEvidence':
      return FilterName.CITATION_EVIDENCE
    case 'citationRequest':
      return FilterName.CITATION_REQUEST
    case 'citationRisk':
      return FilterName.CITATION_RISK
    case 'citationTypes':
      return FilterName.CITATION_TYPES
    case 'organization':
      return FilterName.ORGANIZATION
    case 'questionnaireStatus':
      return FilterName.QUESTIONNAIRE_STATUS
    case 'documentation':
      return FilterName.HAS_DOCUMENTATION
    case 'complianceFramework':
      return FilterName.COMPLIANCE_FRAMEWORK
    case 'audit':
      return FilterName.AUDIT
    case 'auditFrameworks':
      return FilterName.AUDIT_FRAMEWORKS
    case 'risk':
      return FilterName.INHERENT_RISK
    case 'reviewed':
      return FilterName.REVIEWED
    case 'riskCategory':
      return FilterName.RISK_CATEGORY
    case 'receptor':
      return FilterName.RECEPTOR
    case 'serviceRole':
      return FilterName.SERVICE_ROLE
    case 'riskTier':
      return FilterName.RISK_TIER
    case 'vendorStatus':
      return FilterName.VENDOR_STATUS
    case 'vendorState':
      return FilterName.VENDOR_STATE
    case 'vendorEvaluation':
      return FilterName.VENDOR_EVALUATION
    case 'attestation':
      return FilterName.ATTESTATION
    case 'smartChecks':
      return FilterName.AI_CONTROL_CHECKS
    case 'source':
      return FilterName.SOURCE
    case 'requestSource':
      return FilterName.REQUEST_SOURCE
    case 'evidenceType':
      return FilterName.EVIDENCE_TYPE
    case 'folderScan':
      return FilterName.FOLDER_SCAN
    case 'dateRange':
      return 'Date Range'
    case 'questionnaireDateRange':
      return 'Due Date'
    case 'control':
      return 'Control'
    case 'associatedRisk':
      return FilterName.ASSOCIATED_RISK
    case 'frameworkType':
      return FilterName.FRAMEWORK_TYPE
    case 'frameworkName':
      return FilterName.NAME
    case 'label':
      return FilterName.LABEL
    case 'section':
      return FilterName.SECTION
    case 'trustPortalRequestAccess':
      return FilterName.TRUST_PORTAL_REQUEST_ACCESS
    case 'trustPortalPermission':
      return FilterName.TRUST_PORTAL_PERMISSION
    case 'trustPortalFeatures':
      return FilterName.TRUST_PORTAL_FEATURES
    case 'audit_run_owner':
      return FilterName.AUDIT_RUN_OWNER
    case 'audit_run_control':
      return FilterName.AUDIT_RUN_CONTROL
    case 'audit_run_status':
      return FilterName.AUDIT_RUN_STATUS
    case 'audit_run_result':
      return FilterName.AUDIT_RUN_RESULT
    case 'company':
      return FilterName.COMPANY
    case 'reportType':
      return FilterName.REPORT_TYPE
    case 'trustServiceCriteria':
      return FilterName.TRUST_SERVICE_CRITERIA
    case 'auditor':
      return FilterName.AUDITOR
    case 'expirationDate':
      return FilterName.EXPIRATION_DATE
    case 'userRole':
      return FilterName.USER_ROLE
    case 'emailNotification':
      return FilterName.EMAIL_NOTIFICATION
    case 'userDepartment':
      return FilterName.USER_DEPARTMENT
    case 'userStatus':
      return FilterName.USER_STATUS
    case 'dashboardFramework':
      return FilterName.DASHBOARD_FRAMEWORK
    case 'dashboardPolicy':
      return FilterName.DASHBOARD_POLICY
    case 'dashboardAudit':
      return FilterName.DASHBOARD_AUDIT
    case 'dashboardRisk':
      return FilterName.DASHBOARD_RISK
    case 'dashboardDepartment':
      return FilterName.DASHBOARD_DEPARTMENT
    case 'dashboardOwner':
      return FilterName.DASHBOARD_OWNER
    case 'dashboardLabel':
      return FilterName.DASHBOARD_LABEL
    case 'dashboardDateRange':
      return FilterName.DASHBOARD_DATE_RANGE
    case 'executionType':
      return FilterName.EXECUTION_TYPE
    case 'playbookType':
      return FilterName.PLAYBOOK_TYPE
    case 'playbookCategory':
      return FilterName.PLAYBOOK_CATEGORY
    case 'visibilities':
      return FilterName.VISIBILITIES
    case 'tags':
      return FilterName.TAGS
    case 'documentScan':
      return 'Filter by Receptor or Folder Scan'
    case 'overviewFramework':
      return FilterName.DASHBOARD_FRAMEWORK
    case 'overviewDateRange':
      return FilterName.DASHBOARD_DATE_RANGE
    case 'activitiesDateRange':
      return FilterName.DASHBOARD_DATE_RANGE
    default:
      return filterKey
  }
}

// Map config keys to FilterParam values
const getFilterParamFromKey = (
  filterKey: keyof FilterBarConfig,
): string | string[] => {
  switch (filterKey) {
    case 'department':
      return FilterParam.DEPARTMENT
    case 'policy':
      return FilterParam.POLICY
    case 'status':
      return FilterParam.STATUS
    case 'active':
      return FilterParam.ACTIVE
    case 'owner':
      return FilterParam.OWNER
    case 'assignee':
      return FilterParam.ASSIGNEE
    case 'questionnaireVendor':
      return FilterParam.QUESTIONNAIRE_VENDOR
    case 'questionnaireAssignee':
      return FilterParam.QUESTIONNAIRE_ASSIGNEE
    case 'activityActor':
      return FilterParam.ACTIVITY_ACTOR
    case 'activityObjectType':
      return FilterParam.ACTIVITY_OBJECT_TYPE
    case 'citationPolicy':
      return FilterParam.CITATION_POLICY
    case 'citationControl':
      return FilterParam.CITATION_CONTROL
    case 'citationEvidence':
      return FilterParam.CITATION_EVIDENCE
    case 'citationRequest':
      return FilterParam.CITATION_REQUEST
    case 'citationRisk':
      return FilterParam.CITATION_RISK
    case 'citationTypes':
      return FilterParam.CITATION_TYPES
    case 'organization':
      return FilterParam.ORGANIZATION
    case 'questionnaireStatus':
      return FilterParam.QUESTIONNAIRE_STATUS
    case 'documentation':
      return [FilterParam.HAS_DOCUMENTATION, FilterParam.IS_CUSTOMIZED]
    case 'complianceFramework':
      return FilterParam.COMPLIANCE_FRAMEWORK
    case 'audit':
      return FilterParam.AUDIT
    case 'auditFrameworks':
      return FilterParam.AUDIT_FRAMEWORKS
    case 'risk':
      return [FilterParam.INHERENT_RISK, FilterParam.RESIDUAL_RISK]
    case 'reviewed':
      return FilterParam.REVIEWED
    case 'riskCategory':
      return FilterParam.RISK_CATEGORY
    case 'receptor':
      return FilterParam.RECEPTOR
    case 'serviceRole':
      return FilterParam.SERVICE_ROLE
    case 'riskTier':
      return FilterParam.RISK_TIER
    case 'vendorStatus':
      return FilterParam.VENDOR_STATUS
    case 'vendorState':
      return FilterParam.VENDOR_STATE
    case 'vendorEvaluation':
      return FilterParam.VENDOR_EVALUATION
    case 'attestation':
      return FilterParam.ATTESTATION
    case 'smartChecks':
      return FilterParam.AI_CONTROL_CHECKS
    case 'source':
      return FilterParam.SOURCE
    case 'requestSource':
      return FilterParam.REQUEST_SOURCE
    case 'evidenceType':
      return FilterParam.EVIDENCE_TYPE
    case 'folderScan':
      return FilterParam.FOLDER_SCAN
    case 'dateRange':
      return FilterParam.DATE_RANGE
    case 'questionnaireDateRange':
      return FilterParam.DATE_RANGE
    case 'control':
      return FilterParam.CONTROL
    case 'associatedRisk':
      return FilterParam.ASSOCIATED_RISK
    case 'frameworkType':
      return FilterParam.FRAMEWORK_TYPE
    case 'frameworkName':
      return FilterParam.NAME
    case 'label':
      return FilterParam.LABEL
    case 'section':
      return FilterParam.SECTION
    case 'trustPortalRequestAccess':
      return FilterParam.TRUST_PORTAL_REQUEST_ACCESS
    case 'trustPortalPermission':
      return FilterParam.TRUST_PORTAL_PERMISSION
    case 'trustPortalFeatures':
      return FilterParam.TRUST_PORTAL_FEATURES
    case 'audit_run_owner':
      return FilterParam.AUDIT_RUN_OWNER
    case 'audit_run_control':
      return FilterParam.AUDIT_RUN_CONTROL
    case 'audit_run_status':
      return FilterParam.AUDIT_RUN_STATUS
    case 'audit_run_result':
      return FilterParam.AUDIT_RUN_RESULT
    case 'company':
      return FilterParam.COMPANY
    case 'reportType':
      return FilterParam.REPORT_TYPE
    case 'trustServiceCriteria':
      return FilterParam.TRUST_SERVICE_CRITERIA
    case 'auditor':
      return FilterParam.AUDITOR
    case 'expirationDate':
      return FilterParam.DATE_RANGE
    case 'userRole':
      return FilterParam.USER_ROLE
    case 'emailNotification':
      return FilterParam.EMAIL_NOTIFICATION
    case 'userDepartment':
      return FilterParam.USER_DEPARTMENT
    case 'userStatus':
      return FilterParam.USER_STATUS
    case 'executionType':
      return FilterParam.EXECUTION_TYPE
    case 'playbookType':
      return FilterParam.PLAYBOOK_TYPE
    case 'playbookCategory':
      return FilterParam.PLAYBOOK_CATEGORY
    case 'visibilities':
      return FilterParam.VISIBILITIES
    case 'tags':
      return FilterParam.TAGS
    case 'documentScan':
      return FilterParam.SOURCE
    case 'dashboardLabel':
      return FilterParam.DASHBOARD_LABEL
    case 'overviewFramework':
      return FilterParam.DASHBOARD_FRAMEWORK
    case 'overviewDateRange':
      return FilterParam.DASHBOARD_DATE_RANGE
    case 'activitiesDateRange':
      return FilterParam.DASHBOARD_DATE_RANGE
    default:
      return filterKey
  }
}
